import { AIProductType } from '../types';

export type ContentType = AIProductType | 'comic';

interface BaseStoredContent {
  contentType: ContentType;
  workPaper: {
    id: number;
    title: string;
    author?: string;
    thumbnailUrl?: string;
  };
}

interface TextContentData extends BaseStoredContent {
  contentType: 'analogy' | 'post';
  initialContent: string;
}

interface ComicContentData extends BaseStoredContent {
  contentType: 'comic';
  workId: number;
}

type StoredContentData = TextContentData | ComicContentData;

const STORAGE_KEY = 'ai_content_publish_data';

/**
 * Store AI-generated content for publishing
 */
export function storeContentForPublishing(data: StoredContentData): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error storing content for publishing:', error);
  }
}

/**
 * Retrieve stored content for publishing
 */
export function retrieveStoredContent(): StoredContentData | null {
  try {
    const data = window.sessionStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    return JSON.parse(data) as StoredContentData;
  } catch (error) {
    console.error('Error retrieving stored content:', error);
    clearStoredContent();
    return null;
  }
}

/**
 * Clear stored content
 */
export function clearStoredContent(): void {
  window.sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Legacy support: read old storage keys and migrate
 */
export function retrieveLegacyContent(): StoredContentData | null {
  // Check analogy_post_data
  const analogyData = window.sessionStorage.getItem('analogy_post_data');
  if (analogyData) {
    try {
      const parsed = JSON.parse(analogyData);
      if (parsed.fromAnalogy && parsed.initialContent) {
        window.sessionStorage.removeItem('analogy_post_data');
        return {
          contentType: 'analogy',
          initialContent: parsed.initialContent,
          workPaper: parsed.initialPaper,
        };
      }
      if (parsed.fromPost && parsed.initialContent) {
        window.sessionStorage.removeItem('analogy_post_data');
        return {
          contentType: 'post',
          initialContent: parsed.initialContent,
          workPaper: parsed.initialPaper,
        };
      }
    } catch (_e) {
      window.sessionStorage.removeItem('analogy_post_data');
    }
  }

  // Check comic_post_data
  const comicData = window.sessionStorage.getItem('comic_post_data');
  if (comicData) {
    try {
      const parsed = JSON.parse(comicData);
      if (parsed.fromComic && parsed.workId) {
        window.sessionStorage.removeItem('comic_post_data');
        return {
          contentType: 'comic',
          workId: parsed.workId,
          workPaper: parsed.workPaper,
        };
      }
    } catch (_e) {
      window.sessionStorage.removeItem('comic_post_data');
    }
  }

  return null;
}
